/**
 * <clinic-dissolve>: a before/after that dissolves, driven from outside.
 *
 * Two real photographs, the client's pair before and after. With WebGL the
 * "before" burns away along a grain field, left to right but never in a
 * straight line, so the clean spreads the way it does on the bench. The
 * section that owns it calls setProgress(0..1) from scroll.
 *
 * Without WebGL (unsupported, reduced motion, data saver, low-power phone,
 * texture or context failure) it is a plain accessible slider: a range input
 * clipping the "after" photo. The images are in the markup either way, so
 * the content never depends on the canvas.
 *
 * The dissolve edge is monochrome by rule: black, cream and white only
 * (CLAUDE.md rule 5). No orange "burn".
 */
import { motionEnabled, lite, reducedMotion } from '@theme/clinic-motion';

const VERT = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG = `
precision mediump float;
varying vec2 vUv;
uniform sampler2D uBefore;
uniform sampler2D uAfter;
uniform float uProgress;
uniform float uEdge;
uniform vec2 uCoverB;
uniform vec2 uCoverA;
uniform float uAspect;

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise(p);
    p *= 2.03;
    a *= 0.5;
  }
  return v;
}

vec2 cover(vec2 uv, vec2 s) {
  return (uv - 0.5) * s + 0.5;
}

void main() {
  vec3 before = texture2D(uBefore, cover(vUv, uCoverB)).rgb;
  vec3 after = texture2D(uAfter, cover(vUv, uCoverA)).rgb;

  float n = fbm(vec2(vUv.x * uAspect, vUv.y) * 3.0);
  float field = n * 0.62 + vUv.x * 0.38;
  float p = uProgress * (1.0 + uEdge * 2.0) - uEdge;
  float t = smoothstep(p - uEdge, p + uEdge, field);

  vec3 col = mix(after, before, t);
  float front = 1.0 - abs(t * 2.0 - 1.0);
  col = mix(col, col * 0.86, front * 0.7);
  gl_FragColor = vec4(col, 1.0);
}`;

export class ClinicDissolve extends HTMLElement {
  connectedCallback() {
    // ScrollTrigger's pin wraps the stage in a spacer, which detaches and
    // re-attaches this element. Treat that as a move, not a removal.
    clearTimeout(this.teardownTimer);
    if (this.initialized) return;
    this.initialized = true;

    this.before = this.querySelector('[data-before]') || this.querySelector('.clinic-dissolve__img:not(.clinic-dissolve__after)');
    this.after = this.querySelector('[data-after]') || this.querySelector('.clinic-dissolve__after');
    this.canvas = this.querySelector('canvas');
    this.range = this.querySelector('input[type="range"]');
    this.progress = 0;

    this.range?.addEventListener('input', () => this.setSlider(Number(this.range.value) / 100));

    const wantsGL = motionEnabled && !lite && !reducedMotion() && this.canvas && this.before && this.after;
    if (!wantsGL) {
      this.useSlider();
      return;
    }

    this.mode = 'pending';
    this.io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.io.disconnect();
          this.initGL().catch(() => this.useSlider());
        }
      },
      { rootMargin: '100% 0px' }
    );
    this.io.observe(this);
  }

  disconnectedCallback() {
    clearTimeout(this.teardownTimer);
    this.teardownTimer = setTimeout(() => {
      if (!this.isConnected) this.teardown();
    }, 0);
  }

  teardown() {
    this.initialized = false;
    this.io?.disconnect();
    this.ro?.disconnect();
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    const gl = this.gl;
    this.gl = null;
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
  }

  /** 0 = all before, 1 = all after. */
  setProgress(value) {
    this.progress = Math.min(1, Math.max(0, value));
    if (this.mode === 'gl') this.requestDraw();
    else if (this.mode === 'slider') this.setSlider(this.progress, false);
  }

  useSlider() {
    this.mode = 'slider';
    this.setAttribute('data-mode', 'slider');
    if (this.range && !this.range.dataset.touched) this.range.value = '50';
    this.setSlider(Number(this.range?.value ?? 50) / 100, false);
  }

  setSlider(value, fromUser = true) {
    if (fromUser && this.range) this.range.dataset.touched = 'true';
    this.style.setProperty('--reveal', `${(value * 100).toFixed(2)}%`);
    if (this.range && !fromUser) this.range.value = String(Math.round(value * 100));
  }

  async initGL() {
    const gl = this.canvas.getContext('webgl', {
      alpha: false,
      antialias: false,
      premultipliedAlpha: false,
      powerPreference: 'low-power',
      failIfMajorPerformanceCaveat: true,
    });
    if (!gl) throw new Error('no webgl');
    this.gl = gl;

    const compile = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) || 'shader');
      return shader;
    };
    const program = gl.createProgram();
    gl.attachShader(program, compile(gl.VERTEX_SHADER, VERT));
    gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('link');
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(program, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    this.uniforms = {};
    ['uBefore', 'uAfter', 'uProgress', 'uEdge', 'uCoverB', 'uCoverA', 'uAspect'].forEach((name) => {
      this.uniforms[name] = gl.getUniformLocation(program, name);
    });

    const [texBefore, texAfter] = await Promise.all([this.texture(this.before, 0), this.texture(this.after, 1)]);
    if (!this.gl) return;
    this.textures = [texBefore, texAfter];
    gl.uniform1i(this.uniforms.uBefore, 0);
    gl.uniform1i(this.uniforms.uAfter, 1);
    gl.uniform1f(this.uniforms.uEdge, 0.045);

    this.canvas.addEventListener('webglcontextlost', (event) => {
      event.preventDefault();
      cancelAnimationFrame(this.raf);
      this.raf = 0;
      if (this.gl) this.useSlider();
    });

    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(this);
    this.resize();

    this.mode = 'gl';
    this.setAttribute('data-mode', 'gl');
    this.requestDraw();
  }

  async texture(img, unit) {
    const gl = this.gl;
    img.loading = 'eager';
    if (!img.complete || !img.naturalWidth) {
      await new Promise((resolve, reject) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', reject, { once: true });
      });
    }
    await img.decode().catch(() => {});
    const tex = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, img);
    tex.aspect = img.naturalWidth / img.naturalHeight;
    return tex;
  }

  resize() {
    const gl = this.gl;
    if (!gl) return;
    const rect = this.getBoundingClientRect();
    const dprCap = matchMedia('(min-width: 990px)').matches ? 2 : 1.5;
    const dpr = Math.min(window.devicePixelRatio || 1, dprCap);
    const max = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 2048;
    const w = Math.max(1, Math.min(max, Math.round(rect.width * dpr)));
    const h = Math.max(1, Math.min(max, Math.round(rect.height * dpr)));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    gl.viewport(0, 0, w, h);
    const canvasAspect = rect.width / Math.max(rect.height, 1);
    gl.uniform1f(this.uniforms.uAspect, canvasAspect);
    const cover = (imageAspect) => {
      const ratio = canvasAspect / imageAspect;
      return ratio > 1 ? [1, 1 / ratio] : [ratio, 1];
    };
    gl.uniform2fv(this.uniforms.uCoverB, cover(this.textures[0].aspect));
    gl.uniform2fv(this.uniforms.uCoverA, cover(this.textures[1].aspect));
    this.requestDraw();
  }

  requestDraw() {
    if (this.raf || !this.gl) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      const gl = this.gl;
      if (!gl) return;
      gl.uniform1f(this.uniforms.uProgress, this.progress);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    });
  }
}

if (!customElements.get('clinic-dissolve')) customElements.define('clinic-dissolve', ClinicDissolve);
